import { Reset as ResetIcon } from '@carbon/react/icons';
import { MultiSelect, SelectSkeleton } from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon';
import styles from './multi-select-filter.module.scss';

interface MultiSelectFilterProps<T extends { name: string }> {
  /** The multi-select's DOM id. */
  id: string;
  /** Prefix of the skeleton's and the refetch button's test ids (`<prefix>-skeleton`, `<prefix>-refetch`). */
  testIdPrefix: string;
  /** Every option the user can pick. */
  items: T[];
  /** The key a picked option goes by (an id or an abbreviation). */
  getKey: (item: T) => string;
  /** The keys of the options now picked. */
  selectedKeys: string[];
  /** Called with the keys of all picked options when the pick changes. */
  onChange: (keys: string[]) => void;
  /** The visible label above the control; it is also the control's accessible name. */
  title: string;
  /** What the control reads while nothing is picked. */
  label: string;
  /** What it reads when more than one option is picked, given how many (one picked reads that option's name). */
  manyLabel: (count: number) => string;
  /** What it reads instead when the options could not be loaded. */
  errorLabel: string;
  loading: boolean;
  /** Set when the options could not be loaded; the control turns invalid and disabled. */
  error: string | null;
  /** When given, an error shows a refetch button that calls it. */
  onRefetch?: () => void;
  /** The refetch button's tooltip. */
  refetchHint: string;
}

/**
 * A filter that is a multi-select of options loaded from the server: a skeleton while they load, an
 * invalid disabled control with a refetch button when they could not be loaded. The State filter and the
 * Development Area Tags filter are this with their own options and wording.
 * @param {MultiSelectFilterProps<T>} props - The component props.
 * @return {JSX.Element} The filter.
 */
export function MultiSelectFilter<T extends { name: string }>({
  id,
  testIdPrefix,
  items,
  getKey,
  selectedKeys,
  onChange,
  title,
  label,
  manyLabel,
  errorLabel,
  loading,
  error,
  onRefetch,
  refetchHint,
}: MultiSelectFilterProps<T>) {
  const selectedItems = items.filter((item) =>
    selectedKeys.includes(getKey(item))
  );

  // The text on the closed control; the count badge Carbon draws beside it stays.
  let pickedLabel = label;
  if (selectedItems.length === 1) {
    pickedLabel = selectedItems[0].name;
  } else if (selectedItems.length > 1) {
    pickedLabel = manyLabel(selectedItems.length);
  }

  return (
    <div className={styles.filterWrapper}>
      {loading && <SelectSkeleton data-testid={`${testIdPrefix}-skeleton`} />}
      {!loading && (
        <div className={styles.dropdownActionContainer}>
          <div className={styles.dropDown}>
            <MultiSelect
              id={id}
              titleText={title}
              items={items}
              itemToString={(item) => (item ? item.name : '')}
              onChange={({ selectedItems: picked }) =>
                onChange((picked ?? []).map(getKey))
              }
              initialSelectedItems={selectedItems}
              label={error ? errorLabel : pickedLabel}
              invalid={!!error}
              disabled={!!error}
            />
            {error && onRefetch && (
              <div className={styles.refetchButton}>
                <TooltipIcon
                  data-testid={`${testIdPrefix}-refetch`}
                  definition={refetchHint}
                  onClick={onRefetch}
                >
                  <ResetIcon className={styles.resetIcon} />
                </TooltipIcon>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
